import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';

// View jsonchema by JsonToTable (Review-Testing: formData, globals)
import { JsonToTable } from 'react-json-to-table';

// material-ui accordian
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

/**
 * How to create Context API
 * 1) Create a context using createContext() method.
 * 2) Provide context value at a hign-level in component tree.
 * 3) Consume the context value using the render props pattern.
 */
const DialogContext = React.createContext();

// Dialog-imperatively Adopt from https://stackoverflow.com/questions/63737526/material-ui-how-to-open-dialog-imperatively-programmatically

function DialogContainer(props) {
  const {
    children,
    title,
    summary = null,
    detail = null,
    open,
    onClose,
    handleClose,
    onEscapeKeyDown,
    onKill,
  } = props;
  const c = '<p>Test</p>';
  const ret = children ? (
    <Dialog
      open={open}
      disableBackdropClick={true}
      onClose={onClose}
      onExited={onKill}
    >
      {children}
    </Dialog>
  ) : (
    <Dialog
      maxWidth={'lg'}
      fullWidth={true}
      open={open}
      onClose={onClose}
      onExited={onKill}
      disableBackdropClick={true}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {summary && (
          <React.Fragment>
            <Typography variant="h5" gutterBottom>
              Summary:{' '}
            </Typography>
            <JsonToTable json={summary}></JsonToTable>
          </React.Fragment>
        )}
        {detail && (
          <React.Fragment>
            <Accordion style={{ marginTop: 20 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography variant="h5">
                  Detail:
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <JsonToTable json={detail}></JsonToTable>
              </AccordionDetails>
            </Accordion>
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return ret;
}

const DialogProvider = ({ children }) => {
  const [dialogs, setDialogs] = React.useState([]);

  /**
   * Dialog-Contexts 1, openDialog
   * @param {*} option
   */
  const openDialog = (option) => {
    // Add dialog-id instead dialog-id-exist or new dialog-id couse dialog-id use not to exist
    const dialog = { ...option, open: true };
    setDialogs((dialogs) => {
      // Find dialog-id-exist and remove it prevent duplecate;
      let diaIdIndex = dialogs.findIndex((prop) => prop.id === option.id);
      if (diaIdIndex > -1) {
        dialogs.splice(diaIdIndex, 1);
      }
      return [...dialogs, dialog];
    });
  };

  // Dialog-Contexts 2, closeDialog
  const closeDialog = (id) => {
    setDialogs((dialogs) => {
      const mutableDialog = dialogs.find((prop) => prop.id === id);
      if (!mutableDialog) return dialogs;
      if (mutableDialog.onClose) mutableDialog.onClose();

      let diaIdIndex = dialogs.findIndex((prop) => prop.id === id);
      if (diaIdIndex > -1) {
        dialogs.splice(diaIdIndex, 1);
      }

      return [...dialogs, { ...mutableDialog, open: false }];
    });
  };

  //Keep id use to generate dialog
  const idRef = React.useRef([]);

  // Integrate Dialog-Contexts (openDialog, closeDialog)
  // to (array) contextValue
  const contextValueRef = React.useRef([openDialog, closeDialog]);

  return (
    <DialogContext.Provider value={contextValueRef.current}>
      {children}
      {dialogs.map((dialog, i) => {
        return (
          <DialogContainer
            key={i}
            {...dialog}
            handleClose={(e) => {
              e.preventDefault();
              closeDialog(dialog.id);
            }}
          />
        );
      })}
    </DialogContext.Provider>
  );
};

// Create DialogConsumer component, let child component use dialog-contexts
const DialogConsumer = DialogContext.Consumer;

// Create useDialog (by useContext) that includes dialog-contexts (openDialog, closeDialog)
// useDialog let child component use dialog-contexts with-out DialogConsumer component
const useDialog = () => React.useContext(DialogContext);

// Export DialogProvider, DialogConsumer & useDialog
export { DialogProvider, DialogConsumer, useDialog };
